import React, { ReactNode } from "react";
import { Stack, Typography } from "@mui/material";
import { FeatureHeader } from "../components-new";

interface PageShellProps {
  /**
   * Page title displayed on the left side of the header
   */
  title: string | ReactNode;

  /**
   * Content displayed on the right side of the header (buttons, search, filters, etc.)
   */
  headerActions?: ReactNode;

  /**
   * Breadcrumbs component to display above the header (optional)
   */
  breadcrumbs?: ReactNode;

  /**
   * Main page content displayed below the header
   */
  children: ReactNode;

  /**
   * Additional CSS classes for the container
   */
  className?: string;

  /**
   * Padding for the content area (default: "px-4")
   */
  contentPadding?: string;
}

/**
 * PageShell - A reusable component that provides a uniform layout for all pages
 * 
 * Features:
 * - Consistent header layout with title on left and actions on right
 * - Responsive design (stacks vertically on mobile, horizontal on desktop)
 * - Optional breadcrumbs support
 * - Consistent spacing and padding
 * 
 * @example
 * <PageShell
 *   title="Service Requests"
 *   headerActions={
 *     <>
 *       <IconButton onClick={handleSettings}>
 *         <i className="fas fa-cog" />
 *       </IconButton>
 *       <AddNewButton text="Create SR" onButtonClick={handleCreate} />
 *     </>
 *   }
 * >
 *   <ServiceRequestsList />
 * </PageShell>
 */
export const PageShell: React.FC<PageShellProps> = ({
  title,
  headerActions,
  breadcrumbs,
  children,
  className = "",
  contentPadding = "px-4",
}) => {
  return (
    <div className={className}>
      {/* Breadcrumbs (optional) - Sticky positioning */}
      {breadcrumbs && (
        <div className="sticky top-[4rem] z-20 [&_nav]:mb-0 [&_nav]:!top-0 [&_nav]:!shadow-none [&_nav]:!border-b-0 [&_nav]:bg-transparent">
          {breadcrumbs}
        </div>
      )}

      {/* Main content container */}
      <div className={contentPadding}>
        {/* Header section */}
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          className="w-full"
        >
          <div className="w-full flex flex-col justify-between md:flex-row md:justify-between md:items-center">
            {/* Left side: Page title */}
            {typeof title === "string" ? (
              <Typography
                variant={"h2"}
                color={"background"}
                className={"py-4 text-xl"}
              >
                {title}
              </Typography>
            ) : (
              <div className="py-4">{title}</div>
            )}

            {/* Right side: Header actions (buttons, search, filters, etc.) */}
            {headerActions && (
              <>
                {headerActions}
              </>
            )}
          </div>
        </Stack>

        {/* Page content */}
        <div>{children}</div>
      </div>
    </div>
  );
};

export default PageShell;

